<script setup>
import SectionHero from '@/components/SectionHero.vue';
import PricingTable from '@/components/PricingTable.vue';
import PricingTableRow from '@/components/PricingTableRow.vue';
import DomainPricingTable from '@/components/DomainPricingTable.vue';

const props = defineProps(['domainPricing']);

import {useRouting} from "@/composables/routing.js";
const {href,url} = useRouting();

import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3';
</script>

<template>
    <Head>
        <title>Pricing overview</title>
        <meta name="description" content="Find an overview of our pricing for Spaces, databases, cronjobs, cloud DNS zones and domain registration">
        <link rel="canonical" :href="url('/pricing', 'en')">
        <link rel="alternate" hreflang="de" :href="url('/pricing', 'de')">
        <link rel="alternate" hreflang="en" :href="url('/pricing', 'en')">
    </Head>
    <div>
        <section-hero title="All cloud prices at a glance"
                      subtitle="Our cloud hosting solutions are suitable for every budget. Almost all services are billed flexibly per hour and have a monthly maximum price if the service is used for the entire month without scaling. We make managed cloud hosting affordable."
        >
        </section-hero>

        <section class="mt-24 lg:mt-36">
            <div class="container px-4 mx-auto">
                <h2 class="font-semibold text-4xl text-black space-x-3 mb-2">
                    Register an account
                </h2>

                <div class="max-w-[50rem]">

                    <p class="text-lg">
                        We charge a one-off setup fee for registering an account.
                        If the account is deleted within 30 days, the setup fee will be refunded.
                    </p>

                    <table class="table-fixed w-full mt-4">
                        <thead class="text-left text-lg border-b-2 border-primary">
                        <tr class="mx-6">
                            <th>Name</th>
                        </tr>
                        </thead>
                        <tbody class="text-lg">
                        <tr>
                            <td class="py-3">
                                Set-up fee
                            </td>
                            <td class="py-3 text-right pr-5" colspan="2">
                                one-off: 19,00 €
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

            </div>
        </section>

        <section class="mt-24 lg:mt-36">
            <div class="container px-4 mx-auto">
                <h2 class="font-semibold text-4xl text-black space-x-3 mb-2">
                    Spaces
                </h2>

                <div>
                    <p class="text-lg max-w-[60rem]">
                        Spaces are our further development of the web space.
                        RAM & CPU are combined into one performance level.
                        The storage can be expanded in GB increments at any time.
                        Traffic is included (fair use)
                    </p>

                    <pricing-table>
                        <pricing-table-row name="Performance-Level each (RAM: 128 MB, vCPU: 0.0625)"
                                           hour="0,0018"
                                           month="1,09"
                        />
                        <pricing-table-row name="Storage (each GB)"
                                           hour="0,0004"
                                           month="0,28"
                        />
                    </pricing-table>
                </div>

            </div>
        </section>

        <section class="mt-24 lg:mt-36">
            <div class="container px-4 mx-auto">
                <h2 class="font-semibold text-4xl text-black space-x-3 mb-2">
                    Databases
                </h2>

                <div>
                    <p class="text-lg max-w-[60rem]">
                        We do not charge any additional fees for our managed cloud databases.
                        You only pay for the performance level (RAM & CPU) and the storage.
                        The storage can be expanded in GB increments at any time.
                    </p>

                    <pricing-table>
                        <pricing-table-row name="Performance-Level each (RAM: 128 MB, vCPU: 0.0625)"
                                           hour="0,0018"
                                           month="1,09"
                        />
                        <pricing-table-row name="Storage (each GB)"
                                           hour="0,0004"
                                           month="0,28"
                        />
                    </pricing-table>
                </div>
            </div>
        </section>

        <section class="mt-24 lg:mt-36">
            <div class="container px-4 mx-auto">
                <h2 class="font-semibold text-4xl text-black space-x-3 mb-2">
                    Cronjobs
                </h2>

                <div>
                    <p class="text-lg max-w-[60rem]">
                        Cronjobs have their own performance allocation. Cronjobs are billed for as long
                        as they are created. The price therefore does not refer to the execution but to the
                        allocation of the cronjob regardless of the execution interval.
                    </p>

                    <pricing-table>
                        <pricing-table-row name="Performance-Level each (RAM: 128 MB, vCPU: 0.0625)"
                                           hour="0,0012"
                                           month="0,79"
                        />
                    </pricing-table>
                </div>
            </div>
        </section>

        <section class="mt-24 lg:mt-36">
            <div class="container px-4 mx-auto">
                <h2 class="font-semibold text-4xl text-black space-x-3 mb-2">
                    Cloud DNS-Zone
                </h2>

                <div>
                    <p class="text-lg max-w-[60rem]">
                        Both for domains registered with us and for external domains, you can use a cloud DNS zone
                        managed by us. This is resolved on a global anycast DNS infrastructure and is therefore
                        particularly fast and fail-safe.
                    </p>

                    <pricing-table>
                        <pricing-table-row name="Cloud DNS-Zone"
                                           hour="0,0003"
                                           month="0,20"
                        />
                    </pricing-table>
                </div>
            </div>
        </section>

        <section class="mt-24 lg:mt-36">
            <div class="container px-4 mx-auto">
                <h2 class="font-semibold text-4xl text-black space-x-3 mb-2">
                    Domain registration
                </h2>

                <div>
                    <p class="text-lg max-w-[60rem]">
                        With our domain robot you can register your desired domains in real time from over 500 domain
                        extensions. Domains are automatically renewed by one year if they are not cancelled.
                    </p>

                    <domain-pricing-table :domainPricing="domainPricing" />
                </div>
            </div>
        </section>
    </div>

</template>
